import { useState } from "react";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { ShieldCheck, Copy, Check, AlertTriangle, Clock } from "lucide-react";
import QRCode from "qrcode";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeInputOtp } from "@/Components/NextBase/LimeInputOtp";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/api-client";
import { t } from "@lingui/core/macro";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";

type MfaSetupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isRequired?: boolean;
  gracePeriodRemainingDays?: number | null;
};

type SetupStep = "intro" | "qr" | "verify" | "recovery" | "done";

export const MfaSetupModal = ({
  isOpen,
  onClose,
  isRequired = false,
  gracePeriodRemainingDays,
}: MfaSetupModalProps) => {
  const [step, setStep] = useState<SetupStep>("intro");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState("");
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const { mutateAsync: setupMfa, isPending: isSettingUp } =
    api.mfa.useMfaSetup();

  const { mutateAsync: verifySetup, isPending: isVerifying } =
    api.mfa.useMfaVerifySetup();

  const handleStartSetup = async () => {
    try {
      const result = await setupMfa();
      let manualEntrySecret: string | null = null;

      try {
        manualEntrySecret = new URL(result.otpauthUrl).searchParams.get(
          "secret",
        );
      } catch (error) {
        console.error("Failed to extract MFA secret from otpauth URL:", error);
      }

      setSecretKey(manualEntrySecret);
      setRecoveryCodes([]);

      const dataUrl = await QRCode.toDataURL(result.otpauthUrl, {
        width: 256,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      });
      setQrDataUrl(dataUrl);
      setStep("qr");
    } catch {
      addToast({
        title: t`Error`,
        description: t`Failed to start MFA setup. Please try again.`,
        color: "danger",
      });
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;

    try {
      const result = await verifySetup({ code: verifyCode });

      if (result.recoveryCodes) {
        setRecoveryCodes(result.recoveryCodes);
      }
      setStep("recovery");
    } catch {
      addToast({
        title: "Invalid code",
        description: "The verification code is incorrect. Please try again.",
        color: "danger",
      });
      setVerifyCode("");
    }
  };

  const handleCopyRecoveryCodes = async () => {
    const text = recoveryCodes.join("\n");
    setCopiedCodes(false);

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }

      await navigator.clipboard.writeText(text);
      setCopiedCodes(true);
      window.setTimeout(() => setCopiedCodes(false), 2000);
    } catch (error) {
      setCopiedCodes(false);
      console.error("Failed to copy MFA recovery codes:", error);
      addToast({
        title: "Clipboard unavailable",
        description:
          "Clipboard access was denied. Copy the recovery codes manually from the dialog.",
        color: "warning",
      });
      window.prompt("Copy your recovery codes manually:", text);
    }
  };

  const handleComplete = () => {
    queryClient.invalidateQueries({ queryKey: ["mfa-status"] });
    setStep("done");
  };

  const handleDone = () => {
    setStep("intro");
    setQrDataUrl(null);
    setSecretKey(null);
    setRecoveryCodes([]);
    setVerifyCode("");
    setCopiedCodes(false);
    setCopiedSecret(false);
    onClose();

    localStorage.removeItem("usr_token");
    localStorage.removeItem("pos-businessPremiseId");
    localStorage.removeItem("pos-electronicDeviceId");

    window.location.reload();
  };

  // The modal is only mounted while open (`{showMfaSetup && <MfaSetupModal …/>}`
  // in Dashboard), so it unmounts — resetting step/verifyCode — when it closes.
  // No effect needed to reset on close.
  const isCloseBlocked = (isRequired && step !== "done") || step === "recovery";

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={isCloseBlocked ? undefined : handleDone}
      isDismissable={!isCloseBlocked}
      hideCloseButton={isCloseBlocked}
      size="lg"
    >
      <ModalContent>
        {step === "intro" && (
          <>
            <ModalHeader className="flex items-center gap-2">
              <ShieldCheck className="text-primary" size={24} />
              <span>Set Up Two-Factor Authentication</span>
            </ModalHeader>
            <ModalBody>
              <p className="text-default-600">
                Two-factor authentication adds an extra layer of security to
                your admin account. You will need an authenticator app like
                Google Authenticator, Authy, or 1Password.
              </p>

              {gracePeriodRemainingDays != null && (
                <div
                  className={`mt-2 flex items-start gap-2 rounded-lg p-3 ${
                    gracePeriodRemainingDays === 0
                      ? "bg-danger-50"
                      : gracePeriodRemainingDays <= 3
                        ? "bg-warning-50"
                        : "bg-default-100"
                  }`}
                >
                  <Clock
                    className={`mt-0.5 shrink-0 ${
                      gracePeriodRemainingDays === 0
                        ? "text-danger"
                        : gracePeriodRemainingDays <= 3
                          ? "text-warning"
                          : "text-default-500"
                    }`}
                    size={18}
                  />
                  <p
                    className={`text-sm ${
                      gracePeriodRemainingDays === 0
                        ? "text-danger-700"
                        : gracePeriodRemainingDays <= 3
                          ? "text-warning-700"
                          : "text-default-600"
                    }`}
                  >
                    {gracePeriodRemainingDays === 0
                      ? "Your grace period has expired. You must set up MFA now to continue using admin features."
                      : gracePeriodRemainingDays === 1
                        ? "You have less than 1 day remaining to set up MFA before it becomes mandatory."
                        : `You have ${gracePeriodRemainingDays} days remaining to set up MFA before it becomes mandatory.`}
                  </p>
                </div>
              )}

              {isRequired && gracePeriodRemainingDays == null && (
                <div className="bg-warning-50 mt-2 flex items-start gap-2 rounded-lg p-3">
                  <AlertTriangle
                    className="text-warning mt-0.5 shrink-0"
                    size={18}
                  />
                  <p className="text-warning-700 text-sm">
                    MFA is required for administrative access. Please complete
                    the setup to continue using admin features.
                  </p>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {!isRequired && (
                <Button variant="flat" onPress={handleDone}>
                  Later
                </Button>
              )}
              <Button
                color="primary"
                onPress={handleStartSetup}
                isLoading={isSettingUp}
              >
                Begin Setup
              </Button>
            </ModalFooter>
          </>
        )}

        {step === "qr" && (
          <>
            <ModalHeader>Scan QR Code</ModalHeader>
            <ModalBody>
              <p className="text-default-600">
                Open your authenticator app and scan the QR code below.
              </p>
              <div className="flex justify-center py-4">
                {qrDataUrl && (
                  <img
                    src={qrDataUrl}
                    alt="MFA QR Code"
                    className="border-default-200 rounded-lg border"
                    width={256}
                    height={256}
                  />
                )}
              </div>
              {secretKey && (
                <div className="border-default-200 bg-default-50 rounded-lg border p-3">
                  <p className="text-default-500 mb-1 text-xs">
                    Can&apos;t scan? Enter this key manually in your
                    authenticator app:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-sm break-all select-all">
                      {secretKey}
                    </code>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={async () => {
                        setCopiedSecret(false);

                        try {
                          if (!navigator.clipboard?.writeText) {
                            throw new Error("Clipboard API unavailable");
                          }

                          await navigator.clipboard.writeText(secretKey);
                          setCopiedSecret(true);
                          window.setTimeout(() => setCopiedSecret(false), 2000);
                        } catch (error) {
                          setCopiedSecret(false);
                          console.error(
                            "Failed to copy MFA secret key:",
                            error,
                          );
                          addToast({
                            title: "Clipboard unavailable",
                            description:
                              "Clipboard access was denied. Copy the secret key manually from the dialog.",
                            color: "warning",
                          });
                          window.prompt(
                            "Copy your secret key manually:",
                            secretKey,
                          );
                        }
                      }}
                    >
                      {copiedSecret ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onPress={() => setStep("verify")}>
                Next
              </Button>
            </ModalFooter>
          </>
        )}

        {step === "verify" && (
          <>
            <ModalHeader>Verify Setup</ModalHeader>
            <ModalBody>
              <p className="text-default-600">
                Enter the 6-digit code from your authenticator app to confirm
                the setup.
              </p>
              <div className="mt-4 flex justify-center">
                <LimeInputOtp
                  length={6}
                  variant="bordered"
                  value={verifyCode}
                  onChange={(val: string) => setVerifyCode(val)}
                  onComplete={handleVerify}
                  data-identifier="mfa-verify-code"
                  autoFocus={!isMobileSafari && !isNativePlatform()}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={() => setStep("qr")}>
                Back
              </Button>
              <Button
                color="primary"
                onPress={handleVerify}
                isLoading={isVerifying}
                isDisabled={verifyCode.length !== 6}
              >
                Verify
              </Button>
            </ModalFooter>
          </>
        )}

        {step === "recovery" && (
          <>
            <ModalHeader>Save Recovery Codes</ModalHeader>
            <ModalBody>
              <div className="bg-warning-50 flex items-start gap-2 rounded-lg p-3">
                <AlertTriangle
                  className="text-warning mt-0.5 shrink-0"
                  size={18}
                />
                <p className="text-warning-700 text-sm">
                  Save these recovery codes in a secure location. Each code can
                  only be used once. If you lose access to your authenticator
                  app, you will need these codes to log in.
                </p>
              </div>
              <div className="border-default-200 bg-default-50 mt-4 rounded-lg border p-4">
                <div className="grid grid-cols-2 gap-2">
                  {recoveryCodes.map((code, i) => (
                    <code
                      key={i}
                      className="bg-default-100 rounded px-3 py-1.5 text-center font-mono text-sm"
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
              <Button
                variant="flat"
                className="mt-2 w-full"
                onPress={handleCopyRecoveryCodes}
                startContent={
                  copiedCodes ? <Check size={16} /> : <Copy size={16} />
                }
              >
                {copiedCodes ? "Copied!" : "Copy All Codes"}
              </Button>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onPress={handleComplete}>
                I have saved my codes
              </Button>
            </ModalFooter>
          </>
        )}

        {step === "done" && (
          <>
            <ModalHeader className="flex items-center gap-2">
              <ShieldCheck className="text-success" size={24} />
              <span>MFA Enabled</span>
            </ModalHeader>
            <ModalBody>
              <p className="text-default-600">
                Two-factor authentication has been successfully enabled for your
                account. You will be asked for a verification code on your next
                login.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onPress={handleDone}>
                Done
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
};
